import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './SpecialCaseModal.css';
import './SpecialCaseModal.scroll.css';
import DateBox from './DateBox';

// Canonical values sent to Django/manufacturing. Labels remain translated in the UI.
const SURGERY_TYPES = ['Endoscopic', 'Reconstruction', 'Shunt / Hydrocephalus'];
const SURGERY_TYPE_LABEL_KEYS = {
  Endoscopic: 'specialCase.surgeryType.endoscopic',
  Reconstruction: 'specialCase.surgeryType.reconstruction',
  'Shunt / Hydrocephalus': 'specialCase.surgeryType.shunt',
};

const SPECIAL_ISSUES = [
  'Hematoma',
  'Hemangioma - Location (For Either)',
  'Bone Loss / Bone Defect',
  'Syndromic',
  'Other',
];
const SPECIAL_ISSUE_LABEL_KEYS = {
  Hematoma: 'specialCase.specialIssue.hematoma',
  'Hemangioma - Location (For Either)': 'specialCase.specialIssue.hemangiomaLocation',
  'Bone Loss / Bone Defect': 'specialCase.specialIssue.boneLoss',
  Syndromic: 'specialCase.specialIssue.syndromic',
  Other: 'specialCase.specialIssue.other',
};

/* ---- Date helpers ----
   UI display via DateBox (YYYY/MM/DD)
   Internal:   YYYY-MM-DD (ISO)
   Outbound:   MM/DD/YYYY (US) */
function isoToUS(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}

function anyToISO(s) {
  if (!s) return '';
  const v = String(s).trim();
  let m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = v.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[1]}-${m[2]}`;
  return '';
}

const SpecialCaseModal = ({ onClose, onSave, initial }) => {
  const { t } = useTranslation();

  const tx = (key, fallback) => {
    const v = t(key, { defaultValue: fallback });
    return (v === key || v == null || v === '') ? fallback : v;
  };

  const [surgeryTypes, setSurgeryTypes] = useState([]);
  const [specialIssues, setSpecialIssues] = useState([]);
  const [hemangiomaLocation, setHemangiomaLocation] = useState('');
  const [surgeryDateISO, setSurgeryDateISO] = useState('');
  const [suture, setSuture] = useState('');
  const [surgeon, setSurgeon] = useState('');
  const [notes, setNotes] = useState('');

  const hemangiomaChecked = useMemo(
    () => specialIssues.includes('Hemangioma - Location (For Either)'),
    [specialIssues]
  );

  // Rehydrate previously entered Special Case values when the form is reopened.
  // Demographic values in `initial` are still display-only and remain auto-filled.
  useEffect(() => {
    setSurgeryTypes(Array.isArray(initial?.surgery_types) ? initial.surgery_types : []);
    setSpecialIssues(Array.isArray(initial?.special_issues) ? initial.special_issues : []);
    setHemangiomaLocation(initial?.hemangioma_location || '');
    setSuture(initial?.suture || '');
    setSurgeon(initial?.surgeon || '');
    setNotes(initial?.notes || '');

    const sourceDate = initial?.surgery_date || '';
    setSurgeryDateISO(anyToISO(sourceDate));
  }, [initial]);

  const toggleArrayValue = (arr, value, setter) => {
    setter(arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
  };

  const handleSave = () => {
    onSave({
      special_case: true,
      specialCase: true,
      is_special: true,
      surgery_types: surgeryTypes,
      special_issues: specialIssues,
      hemangioma_location: hemangiomaChecked ? hemangiomaLocation.trim() : '',
      surgery_date: isoToUS(surgeryDateISO),
      suture: suture.trim(),
      surgeon: surgeon.trim(),
      notes: notes.trim(),
    });
  };

  return (
    <div className="rmk-overlay scm-overlay scm-overlay-scroll" role="dialog" aria-modal="true" aria-labelledby="scm-title">
      <div className="rmk-modal scm-modal">
        <div className="rmk-header scm-header">
          <div className="scm-header-copy">
            <span className="scm-eyebrow">{t('specialCase.manufacturingOrder')}</span>
            <h3 id="scm-title">{tx('specialCase.header', 'Special Case')}</h3>
            <p>{t('specialCase.subtitle')}</p>
          </div>
          <button className="rmk-close scm-close" onClick={onClose} aria-label={tx('close', 'Close')} type="button">
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="rmk-body scm-body">
          <div className="scm-alert" role="note">
            <div className="scm-alert-icon" aria-hidden="true">!</div>
            <div>
              <strong>{t('specialCase.dsiReminder')}</strong>
              <span>
                {tx(
                  'specialCase.rememberNote',
                  'Please remember to circle shunt, area of bone loss, hemangioma, hematoma (etc.) on stockinet in red during DSi.'
                )}
              </span>
            </div>
          </div>

          <section className="scm-patient-card" aria-label={t('specialCase.patientSummary')}>
            <div className="scm-patient-item scm-patient-item--wide">
              <span>{tx('specialCase.patientNameLabel', 'Patient Name')}</span>
              <strong>{initial?.patientName || t('specialCase.autoFilled')}</strong>
            </div>
            <div className="scm-patient-item">
              <span>{tx('specialCase.dobLabel', 'DOB')}</span>
              <strong>{initial?.dob || t('specialCase.autoFilled')}</strong>
            </div>
            <div className="scm-patient-item">
              <span>{tx('specialCase.ageLabel', 'Age')}</span>
              <strong>{initial?.ageDisplay || initial?.age || t('specialCase.autoFilled')}</strong>
            </div>
            <div className="scm-patient-item">
              <span>{tx('specialCase.centerLabel', 'Center')}</span>
              <strong>{initial?.center || t('specialCase.autoFilled')}</strong>
            </div>
          </section>

          <section className="rmk-block scm-section">
            <div className="scm-section-heading">
              <div>
                <span className="scm-step">01</span>
                <h4>{tx('specialCase.surgeryTypeLabel', 'Surgery Type')}</h4>
              </div>
              <p>{t('specialCase.selectAll')}</p>
            </div>
            <div className="rmk-checkgrid scm-option-grid">
              {SURGERY_TYPES.map(opt => {
                const checked = surgeryTypes.includes(opt);
                return (
                  <label key={opt} className={`rmk-check scm-option ${checked ? 'is-selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleArrayValue(surgeryTypes, opt, setSurgeryTypes)}
                    />
                    <span className="scm-checkmark" aria-hidden="true">{checked ? '✓' : ''}</span>
                    <span className="rmk-check-text">{tx(SURGERY_TYPE_LABEL_KEYS[opt], opt)}</span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="rmk-block scm-section">
            <div className="scm-section-heading">
              <div>
                <span className="scm-step">02</span>
                <h4>{tx('specialCase.specialIssueLabel', 'Special Issue')}</h4>
              </div>
              <p>{t('specialCase.issueHint')}</p>
            </div>
            <div className="rmk-checkgrid scm-option-grid scm-option-grid--issues">
              {SPECIAL_ISSUES.map(opt => {
                const checked = specialIssues.includes(opt);
                return (
                  <label key={opt} className={`rmk-check scm-option ${checked ? 'is-selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleArrayValue(specialIssues, opt, setSpecialIssues)}
                    />
                    <span className="scm-checkmark" aria-hidden="true">{checked ? '✓' : ''}</span>
                    <span className="rmk-check-text">{tx(SPECIAL_ISSUE_LABEL_KEYS[opt], opt)}</span>
                  </label>
                );
              })}
            </div>

            {hemangiomaChecked && (
              <div className="scm-inline-field">
                <label htmlFor="scm-hemangioma-location">
                  {tx('specialCase.specialIssue.hemangiomaLocation', 'Hemangioma Location')}
                </label>
                <input
                  id="scm-hemangioma-location"
                  type="text"
                  value={hemangiomaLocation}
                  onChange={(e) => setHemangiomaLocation(e.target.value)}
                  placeholder={tx('special.placeholderRegion', 'e.g., right parietal region')}
                />
              </div>
            )}
          </section>

          <section className="rmk-block scm-section">
            <div className="scm-section-heading">
              <div>
                <span className="scm-step">03</span>
                <h4>{t('specialCase.procedureDetails')}</h4>
              </div>
              <p>{t('specialCase.procedureHint')}</p>
            </div>

            <div className="rmk-grid scm-field-grid">
              <DateBox
                label={tx('specialCase.surgeryDateLabel', 'Surgery Date (YYYY/MM/DD)')}
                valueISO={surgeryDateISO}
                onChangeISO={setSurgeryDateISO}
                placeholder={tx('specialCase.surgeryDatePlaceholder', 'YYYY/MM/DD')}
                name="surgery_date"
                portalId="portal-root"
                withPortal
                popperPlacement="bottom-start"
                popperModifiers={[
                  { name: 'preventOverflow', options: { boundary: 'viewport', rootBoundary: 'document' } },
                  { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
                ]}
              />

              <label className="scm-field">
                <span>{tx('specialCase.sutureLabel', 'Suture')}</span>
                <input
                  type="text"
                  value={suture}
                  onChange={(e) => setSuture(e.target.value)}
                  placeholder={tx('special.placeholderSuture', 'e.g., Sagittal')}
                />
              </label>

              <label className="scm-field">
                <span>{tx('specialCase.surgeonLabel', 'Surgeon')}</span>
                <input
                  type="text"
                  value={surgeon}
                  onChange={(e) => setSurgeon(e.target.value)}
                  placeholder={tx('special.placeholderSurgeon', 'e.g., Dr. Smith')}
                />
              </label>
            </div>
          </section>

          <section className="rmk-block scm-section scm-notes-section">
            <div className="scm-section-heading">
              <div>
                <span className="scm-step">04</span>
                <h4>{tx('specialCase.notesLabel', 'Notes')}</h4>
              </div>
              <p>{t('specialCase.notesPdfHint')}</p>
            </div>
            <label className="scm-notes-field">
              <span className="sr-only">{tx('specialCase.notesLabel', 'Notes')}</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={6}
                placeholder={t('specialCase.notesPlaceholder')}
              />
              <small>{t('specialCase.characters', { count: notes.length.toLocaleString() })}</small>
            </label>
          </section>
        </div>

        <div className="rmk-footer scm-footer">
          <button className="rmk-btn secondary scm-btn scm-btn--secondary" onClick={onClose} type="button">
            {tx('remake.cancel', 'Cancel')}
          </button>
          <button className="rmk-btn scm-btn scm-btn--primary" onClick={handleSave} type="button">
            {t('specialCase.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpecialCaseModal;
